import * as React from "react";

import type { GridRow } from "./gridRow";

interface UseRowExpansionOptions<TData> {
  data: TData[];
  getRowId: (row: TData) => string;
}

export type ExpandableGridRow<TData> = GridRow<TData> & {
  __detail?: boolean;
  __expanded?: boolean;
};

export interface UseRowExpansionReturn<TData> {
  expandedIds: Set<string>;
  isExpanded: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Original rows interleaved with full-width detail rows for expanded items. */
  displayRows: ExpandableGridRow<TData>[];
}

/**
 * Internal helper for `<DataTable>` that turns the consumer's data array into
 * a sequence of grid rows interleaved with full-width detail rows for any
 * currently-expanded items. Resets expansion when the underlying data
 * changes (sort / page / pageSize).
 */
export function useRowExpansion<TData>(
  options: UseRowExpansionOptions<TData>,
): UseRowExpansionReturn<TData> {
  const { data, getRowId } = options;
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(
    () => new Set(),
  );

  React.useEffect(() => {
    setExpandedIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const visibleIds = new Set(data.map(getRowId));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (visibleIds.has(id)) {
          next.add(id);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [data, getRowId]);

  const isExpanded = React.useCallback(
    (id: string) => expandedIds.has(id),
    [expandedIds],
  );

  const toggle = React.useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const displayRows = React.useMemo<ExpandableGridRow<TData>[]>(() => {
    const rows: ExpandableGridRow<TData>[] = [];
    for (const row of data) {
      const id = getRowId(row);
      const expanded = expandedIds.has(id);
      rows.push({
        ...row,
        __detail: false,
        __expanded: expanded,
      });
      if (expanded) {
        rows.push({
          ...row,
          __detail: true,
          __expanded: true,
        });
      }
    }
    return rows;
  }, [data, getRowId, expandedIds]);

  return { expandedIds, isExpanded, toggle, displayRows };
}
